/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader, X } from 'react-feather';

import { useArtistCategories, useArtistList } from '@/Product/hooks/useArtist';
import {
  Artist,
  ArtistDropdownProps,
  ArtistItem,
} from '@/Product/types/productTypes';

const ArtistDropdown: React.FC<ArtistDropdownProps> = ({
  selectedArtists,
  onArtistChange,
  onArtistRemove,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    data: categoryResponse,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } = useArtistCategories();
  const {
    data: artistResponse,
    isLoading: isArtistsLoading,
    error: artistsError,
  } = useArtistList(selectedCategoryId);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategorySelect = (categoryId: number) => {
    if (selectedCategoryId === categoryId) {
      setSelectedCategoryId(null);
    } else {
      setSelectedCategoryId(categoryId);
    }
  };

  const handleArtistSelect = (artistItem: ArtistItem) => {
    const artist: Artist = {
      artistId: artistItem.id,
      artistNameKO: artistItem.artistNameKO,
      artistNameEN: artistItem.artistNameEN,
    };

    const isSelected = isArtistSelected(artistItem.id);
    onArtistChange(artist, !isSelected);
  };

  const isArtistSelected = (artistId: number) => {
    return selectedArtists.some((artist) => artist.artistId === artistId);
  };

  const getDisplayText = () => {
    if (selectedArtists.length === 0) {
      return 'Click to add artists';
    }
    return selectedArtists
      .map((artist) => artist.artistNameKO || artist.artistNameEN)
      .join(', ');
  };

  if (categoriesError) {
    return (
      <div className="relative">
        <div className="h-10 w-full rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600">
          아티스트 카테고리 로드 실패
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        tabIndex={0}
        role="button"
        className={`relative h-10 w-full rounded border border-[#CECECE] px-3 text-sm ${
          disabled
            ? 'cursor-not-allowed bg-gray-100'
            : 'cursor-pointer bg-white'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <input
          type="text"
          className="h-full w-[90%] cursor-pointer border-none bg-transparent text-gray-500 outline-none"
          value={getDisplayText()}
          placeholder="Click to add artists"
          readOnly
          disabled={disabled}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">
          {isCategoriesLoading ? (
            <Loader size={16} className="animate-spin text-gray-500" />
          ) : (
            <ChevronDown
              size={16}
              className={`text-gray-500 transition-transform ${
                isOpen ? 'rotate-180 transform' : ''
              }`}
            />
          )}
        </div>
      </div>

      {/* Selected Artists Tags */}
      {selectedArtists.length > 0 && (
        <div className="mt-2 flex w-full flex-wrap gap-2">
          {selectedArtists.map((artist) => (
            <div
              key={artist.artistId}
              className="flex items-center gap-2 rounded bg-[#F4ECF4] px-2 py-1"
            >
              <p className="text-xs font-normal text-[#1D1D1D]">
                {artist.artistNameKO || artist.artistNameEN}
              </p>
              <button
                type="button"
                className="cursor-pointer bg-transparent"
                onClick={() => onArtistRemove(artist.artistId)}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropdown positioned after tags */}
      {isOpen && !disabled && categoryResponse?.data && (
        <div className="absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded border border-[#CECECE] bg-white shadow-lg">
          {/* Artist Categories */}
          {categoryResponse.data.artistCategoryList.map((category) => (
            <div key={category.id}>
              <div
                tabIndex={0}
                role="button"
                className={`cursor-pointer px-3 py-2 font-medium hover:bg-gray-100 ${
                  selectedCategoryId === category.id ? 'bg-gray-100' : ''
                }`}
                onClick={() => handleCategorySelect(category.id)}
              >
                {category.category}
              </div>

              {/* Artist List for Selected Category */}
              {selectedCategoryId === category.id && (
                <div className="border-t border-gray-200">
                  {isArtistsLoading ? (
                    <div className="flex items-center justify-center px-3 py-4">
                      <Loader
                        size={20}
                        className="animate-spin text-gray-500"
                      />
                    </div>
                  ) : artistsError ? (
                    <div className="px-3 py-2 text-sm text-red-600">
                      아티스트 목록 로드 실패
                    </div>
                  ) : artistResponse?.data?.artistList &&
                    artistResponse.data.artistList.length > 0 ? (
                    artistResponse.data.artistList.map((artist) => (
                      <div
                        tabIndex={0}
                        role="button"
                        key={artist.id}
                        className={`cursor-pointer px-6 py-2 hover:bg-gray-50 ${
                          isArtistSelected(artist.id)
                            ? 'bg-[#F4ECF4] text-[#8f689a]'
                            : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArtistSelect(artist);
                        }}
                      >
                        <span className="text-sm">
                          {artist.artistNameKO || artist.artistNameEN}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="px-6 py-2 text-sm text-gray-500">
                      아티스트가 없습니다.
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArtistDropdown;
